import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  Button,
  Chip,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  Download as DownloadIcon,
  AssessmentOutlined as AssessmentIcon,
  TuneOutlined as TuneIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import PageTitleIcon from "../components/PageTitleIcon";
import { SetupCoachmark } from "../components/selfSetup/SetupCoachmark";
import { useSelfSetupTour } from "../hooks/useSelfSetupTour";
import { HELP_GUIDE_DOWNLOAD_REPORT } from "../constants/knowledgeBaseTryIt";
import { helpI18n } from "../i18n/help";
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from "../utils/helpGuideSession";
import { useDashboardStore } from "../stores/dashboard.store";
import { useOrganizationStore } from "../stores/organization.store";
import { useKPIStore } from "../stores/kpi.store";
import { buildDashboardReportPdf } from "../utils/dashboardReportPdf.helpers";
import ReportCustomizeDialog from "../components/report/ReportCustomizeDialog";
import {
  getReportSectionsForOrgType,
  type ReportPdfSectionId,
} from "../constants/reportCustomize.catalog";
import {
  loadReportPreferences,
  saveReportPreferences,
} from "../utils/reportPreferences.helpers";

const ALL_TIME_VALUE = "ALL_TIME";

const Report: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { t: tHelp } = useTranslation("translation", { i18n: helpI18n });
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganization, fetchOrganizationById } = useOrganizationStore();
  const {
    statistics,
    aggregatedStatistics,
    isLoading,
    isLoadingAggregated,
    selectedMonth,
    fetchDashboardStatistics,
    fetchAggregatedDashboardStatistics,
    setSelectedMonth,
  } = useDashboardStore();
  const { limits } = useKPIStore();
  const { isStep, tourPhase, setTourPhase, completeStep, dismissTour } =
    useSelfSetupTour();
  const showReportTour = isStep("report");

  const [periodValue, setPeriodValue] = useState<string>("");
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [helpReportGuide, setHelpReportGuide] = useState(false);
  const [reportGuidePhase, setReportGuidePhase] = useState<"period" | "download">(
    "period",
  );
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [selectedSections, setSelectedSections] = useState<ReportPdfSectionId[]>(
    () => {
      const orgType = useOrganizationStore.getState().selectedOrganization
        ?.organization_type;
      return orgType ? loadReportPreferences(orgType) : [];
    },
  );

  const isAllTime = periodValue === ALL_TIME_VALUE;
  const showPeriodTip =
    (showReportTour && tourPhase === "period") ||
    (helpReportGuide && reportGuidePhase === "period");
  const showDownloadTip =
    (showReportTour && tourPhase === "download") ||
    (helpReportGuide && reportGuidePhase === "download");

  useEffect(() => {
    const guide = new URLSearchParams(location.search).get("helpGuide");
    if (guide !== HELP_GUIDE_DOWNLOAD_REPORT) return;
    markHelpGuideActive(guide);
    setHelpReportGuide(true);
    setReportGuidePhase("period");
    navigate({ pathname: location.pathname, search: "" }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!showPeriodTip && !showDownloadTip) return;
    const id = showPeriodTip ? "help-report-period" : "help-report-download";
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [showPeriodTip, showDownloadTip]);

  const finishReportGuide = () => {
    if (helpReportGuide) {
      setHelpReportGuide(false);
      setReportGuidePhase("period");
      clearHelpGuideActive();
      return;
    }
    if (showReportTour) {
      completeStep("report");
    }
  };

  /** Close tip without completing the report step (Later / X). */
  const softDismissReportGuide = () => {
    if (helpReportGuide) {
      setHelpReportGuide(false);
      setReportGuidePhase("period");
      clearHelpGuideActive();
      return;
    }
    dismissTour();
  };

  const advanceReportTip = () => {
    if (showReportTour && tourPhase === "period") {
      setTourPhase("download");
      return;
    }
    if (helpReportGuide && reportGuidePhase === "period") {
      setReportGuidePhase("download");
      return;
    }
    finishReportGuide();
  };

  const [availableMonths] = useState(() => {
    const months = [];
    const currentDate = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1
      );
      months.push({
        value: format(date, "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        label: format(date, "MMMM yyyy"),
      });
    }
    return months;
  });

  useEffect(() => {
    const orgType = selectedOrganization?.organization_type;
    if (!selectedOrganization?.id || !orgType) return;

    const currentDate = new Date();
    const defaultMonth = format(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
      "yyyy-MM-dd'T'HH:mm:ss'Z'"
    );
    const monthToUse = selectedMonth || defaultMonth;
    if (monthToUse !== selectedMonth) {
      setSelectedMonth(monthToUse);
    }
    if (!periodValue) {
      setPeriodValue(monthToUse);
    }

    fetchDashboardStatistics(selectedOrganization.id, orgType, monthToUse);
    setSelectedSections(loadReportPreferences(orgType));
  }, [selectedOrganization?.id, selectedOrganization?.organization_type]);

  useEffect(() => {
    if (
      !isAllTime ||
      !selectedOrganization?.id ||
      !selectedOrganization?.organization_type
    ) {
      return;
    }
    fetchAggregatedDashboardStatistics(
      selectedOrganization.id,
      selectedOrganization.organization_type
    );
  }, [
    isAllTime,
    selectedOrganization?.id,
    selectedOrganization?.organization_type,
    fetchAggregatedDashboardStatistics,
  ]);

  const handlePeriodChange = (value: string) => {
    setPeriodValue(value);

    if (value === ALL_TIME_VALUE) {
      return;
    }

    setSelectedMonth(value);
    if (selectedOrganization?.id && selectedOrganization?.organization_type) {
      fetchDashboardStatistics(
        selectedOrganization.id,
        selectedOrganization.organization_type,
        value
      );
    }
  };

  const displayStatistics = isAllTime ? aggregatedStatistics : statistics;
  const isLoadingStatistics = isAllTime ? isLoadingAggregated : isLoading;

  const handleDownloadReport = async () => {
    if (!displayStatistics || !selectedOrganization) return;
    setIsGeneratingReport(true);

    try {
      const freshOrg = await fetchOrganizationById(selectedOrganization.id);
      const orgForReport = freshOrg ?? selectedOrganization;

      const { doc, filename } = await buildDashboardReportPdf({
        statistics: displayStatistics,
        organizationName: orgForReport.name ?? "Organization",
        organizationLogoUrl: orgForReport.logo_url,
        dashboardViewTab: isAllTime ? 1 : 0,
        selectedMonth,
        limits,
        includeSections: selectedSections,
      });
      doc.save(filename);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  if (!selectedOrganization?.id) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <Typography>{t("report.selectOrganization")}</Typography>
      </Box>
    );
  }

  const periodLabel = isAllTime
    ? t("report.allTimeAggregated")
    : availableMonths.find((m) => m.value === selectedMonth)?.label ??
      t("report.selectedPeriod");

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 3,
          p: 3,
          borderRadius: "16px",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <PageTitleIcon icon={<AssessmentIcon />} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: "text.primary" }}>
              {t("report.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("report.subtitle", { name: selectedOrganization.name })}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            gap: 1.5,
          }}
        >
          <Tooltip title={t("report.customizeSubtitle")}>
            <Button
              variant="outlined"
              startIcon={<TuneIcon />}
              onClick={() => setCustomizeOpen(true)}
              aria-label={t("report.customize")}
              sx={{
                borderRadius: "12px",
                px: 2.25,
                py: 1,
                minHeight: 40,
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                gap: 0.75,
              }}
            >
              {t("report.customize")}
              {selectedOrganization.organization_type &&
                selectedSections.length <
                  getReportSectionsForOrgType(
                    selectedOrganization.organization_type,
                  ).length && (
                  <Chip
                    component="span"
                    label={selectedSections.length}
                    size="small"
                    sx={{
                      height: 22,
                      "& .MuiChip-label": {
                        px: 1,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                      },
                    }}
                  />
                )}
            </Button>
          </Tooltip>
          <Stack spacing={0.75} sx={{ minWidth: 220 }}>
            <Typography variant="caption" color="text.secondary">
              {t("report.period")}
            </Typography>
            <SetupCoachmark
              open={showPeriodTip}
              title={
                helpReportGuide
                  ? tHelp("knowledgeBase.downloadReportPeriodTitle")
                  : t("selfSetup.tour.reportPeriodTitle")
              }
              subtitle={
                helpReportGuide
                  ? tHelp("knowledgeBase.downloadReportPeriodSubtitle")
                  : t("selfSetup.tour.reportPeriodSubtitle")
              }
              onDismiss={softDismissReportGuide}
              onNext={advanceReportTip}
              nextLabel={t("selfSetup.tourNext")}
            >
              <Select
                id="help-report-period"
                size="small"
                value={periodValue || selectedMonth || availableMonths[0]?.value}
                onChange={(event) => handlePeriodChange(event.target.value)}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value={ALL_TIME_VALUE}>{t("report.allTime")}</MenuItem>
                {availableMonths.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </SetupCoachmark>
          </Stack>
        </Box>
      </Paper>

      {selectedOrganization.organization_type && (
        <ReportCustomizeDialog
          open={customizeOpen}
          orgType={selectedOrganization.organization_type}
          selectedSections={selectedSections}
          onClose={() => setCustomizeOpen(false)}
          onSave={(next) => {
            setSelectedSections(next);
            saveReportPreferences(next);
            setCustomizeOpen(false);
          }}
        />
      )}

      {isLoadingStatistics && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {!isLoadingStatistics && !displayStatistics && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("report.noData")}
        </Typography>
      )}

      <Grid container spacing={3} alignItems="stretch" sx={{ mb: 3 }}>
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <Card
            elevation={2}
            sx={{
              borderRadius: "16px",
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="flex-start"
                sx={{ flex: 1 }}
              >
                <AssessmentIcon
                  sx={{ fontSize: 48, color: theme.palette.primary.main }}
                />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t("report.cardTitle")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {periodLabel}
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ mt: "auto" }}>
                <SetupCoachmark
                  open={showDownloadTip}
                  title={
                    helpReportGuide
                      ? tHelp("knowledgeBase.downloadReportButtonTitle")
                      : t("selfSetup.tour.reportDownloadTitle")
                  }
                  subtitle={
                    helpReportGuide
                      ? tHelp("knowledgeBase.downloadReportButtonSubtitle")
                      : t("selfSetup.tour.reportDownloadSubtitle")
                  }
                  onDismiss={softDismissReportGuide}
                  onNext={finishReportGuide}
                  nextLabel={t("selfSetup.tourDone")}
                  fullWidth
                >
                  <Button
                    id="help-report-download"
                    variant="contained"
                    fullWidth
                    startIcon={
                      isGeneratingReport ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <DownloadIcon />
                      )
                    }
                    onClick={() => {
                      void handleDownloadReport();
                      if (helpReportGuide || showReportTour) {
                        finishReportGuide();
                      }
                    }}
                    disabled={
                      isGeneratingReport ||
                      (!(helpReportGuide || showReportTour) &&
                        (isLoadingStatistics || !displayStatistics))
                    }
                    sx={{ borderRadius: "12px", py: 1.25 }}
                  >
                    {isGeneratingReport
                      ? t("report.generating")
                      : t("report.download")}
                  </Button>
                </SetupCoachmark>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Report;
